import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Avatar } from "@/components/Avatar";

export const Composer = ({ user, onPosted }) => {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);
  const pick = (e) => {
    const next = [...files, ...Array.from(e.target.files || [])].slice(0, 4);
    if (next.some((f) => f.size > 8 * 1024 * 1024)) toast.error("Each photo must be under 8 MB");
    setFiles(next.filter((f) => f.size <= 8 * 1024 * 1024));
    e.target.value = "";
  };
  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("body", body);
    files.forEach((f) => fd.append("files", f));
    try {
      await api.post("/posts", fd);
      setBody(""); setFiles([]); toast.success("Posted!"); onPosted();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="post-composer" className="rounded-[2rem] border border-[#EAE4DC] bg-white p-5 sm:p-6">
      <div className="flex gap-3">
        <Avatar user={user} className="h-10 w-10" />
        <textarea data-testid="composer-text" value={body} onChange={(e) => setBody(e.target.value)} rows={2} maxLength={2000}
          placeholder={user.role === "tech" ? "Show off a fresh set…" : "Share your nails or ask the techs…"} className="w-full resize-none bg-transparent pt-2 text-sm outline-none" />
      </div>
      {files.length > 0 && (
        <div className="mt-4 grid grid-cols-4 gap-2">
          {files.map((f, i) => (
            <div key={i} className="relative aspect-square overflow-hidden rounded-xl">
              <img src={previews[i]} alt="" className="h-full w-full object-cover" />
              <button data-testid={`composer-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center justify-between border-t border-[#EAE4DC] pt-4">
        <button data-testid="composer-add-photo" onClick={() => input.current?.click()} disabled={files.length >= 4} className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-pink-600 disabled:opacity-40">
          <ImagePlus className="h-5 w-5" /> Photos <span className="font-mono text-xs text-stone-400">{files.length}/4</span>
        </button>
        <input ref={input} data-testid="composer-file-input" type="file" accept="image/*" multiple hidden onChange={pick} />
        <button data-testid="composer-submit" onClick={submit} disabled={busy || (!body.trim() && !files.length)} className="ig-gradient inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium text-white disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Post
        </button>
      </div>
    </div>
  );
};
